import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Minus, Plus, ShoppingBag } from "lucide-react";
import { useShop } from "../context/ShopContext";
import { formatINR } from "../lib/api";
import { toast } from "sonner";

export default function Cart() {
  const { cart, updateQty, removeFromCart, subtotal, appliedCoupon, applyCoupon, removeCoupon } = useShop();
  const nav = useNavigate();
  const [coupon, setCoupon] = useState("");

  const shipping = subtotal >= 999 || subtotal === 0 ? 0 : 79;
  const discount = appliedCoupon && subtotal >= (appliedCoupon.min_order || 0)
    ? Math.round((subtotal * appliedCoupon.discount_percent) / 100 * 100) / 100
    : 0;
  const total = subtotal + shipping - discount;

  const onApply = async () => {
    try {
      const c = await applyCoupon(coupon);
      if (subtotal < c.min_order) {
        toast.error(`Coupon requires minimum order of ${formatINR(c.min_order)}`);
        return;
      }
      toast.success(`${c.code} applied — ${c.description}`);
    } catch {
      toast.error("Invalid coupon code");
    }
  };

  if (cart.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="cart-empty">
        <ShoppingBag size={40} strokeWidth={1.2} className="mx-auto text-brand-maroon" />
        <h1 className="font-serif-brand text-4xl text-brand-charcoal mt-6">Your cart awaits treasures</h1>
        <p className="text-brand-muted mt-2">Discover heirlooms that turn heads at every occasion.</p>
        <Link to="/shop" className="btn-primary inline-block mt-8" data-testid="empty-cart-shop-btn">Explore Jewellery</Link>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12" data-testid="cart-page">
      <p className="label-tiny text-brand-gold">Your Bag</p>
      <h1 className="font-serif-brand text-4xl lg:text-5xl text-brand-charcoal mt-2 mb-10">Shopping Cart · {cart.length} items</h1>

      <div className="grid lg:grid-cols-[1fr_400px] gap-12">
        <div className="space-y-6">
          {cart.map((item) => (
            <div key={item.key} className="flex gap-4 lg:gap-6 py-6 border-b border-brand-gold/25" data-testid={`cart-item-${item.slug}`}>
              <Link to={`/product/${item.slug}`} className="w-24 h-32 lg:w-28 lg:h-36 bg-brand-ivory-soft overflow-hidden shrink-0">
                <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
              </Link>
              <div className="flex-1">
                <Link to={`/product/${item.slug}`} className="font-serif-brand text-lg lg:text-xl text-brand-charcoal hover:text-brand-maroon">{item.name}</Link>
                {item.variant && <p className="text-xs text-brand-muted mt-1">Variant: {item.variant}</p>}
                <p className="text-brand-maroon font-semibold mt-2">{formatINR(item.price)}</p>
                <div className="flex items-center gap-4 mt-3">
                  <div className="flex items-center border border-brand-gold/40">
                    <button onClick={() => updateQty(item.key, item.quantity - 1)} className="px-2.5 py-2" data-testid={`cart-dec-${item.slug}`}><Minus size={12} /></button>
                    <span className="px-4 text-sm">{item.quantity}</span>
                    <button onClick={() => updateQty(item.key, item.quantity + 1)} className="px-2.5 py-2" data-testid={`cart-inc-${item.slug}`}><Plus size={12} /></button>
                  </div>
                  <button onClick={() => removeFromCart(item.key)} className="text-brand-muted hover:text-brand-maroon" data-testid={`cart-remove-${item.slug}`}><Trash2 size={16} strokeWidth={1.5} /></button>
                </div>
              </div>
              <p className="text-brand-charcoal font-semibold whitespace-nowrap">{formatINR(item.price * item.quantity)}</p>
            </div>
          ))}
        </div>

        {/* Summary */}
        <aside className="bg-brand-ivory-soft border border-brand-gold/30 p-6 lg:p-8 h-fit lg:sticky lg:top-32">
          <p className="label-tiny text-brand-gold">Order Summary</p>
          <h2 className="font-serif-brand text-2xl text-brand-charcoal mt-2 mb-6">Review your order</h2>

          <div className="flex gap-2 mb-6">
            <input value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="Coupon code" className="flex-1 px-3 py-2.5 text-sm border border-brand-gold/40 bg-brand-ivory outline-none" data-testid="coupon-input" />
            <button onClick={onApply} className="text-xs tracking-[0.2em] uppercase font-medium px-4 border border-brand-maroon text-brand-maroon hover:bg-brand-maroon hover:text-brand-ivory transition-colors duration-200" data-testid="apply-coupon-btn">Apply</button>
          </div>
          {appliedCoupon && (
            <div className="flex items-center justify-between text-xs mb-4 bg-brand-gold/10 border border-brand-gold/30 px-3 py-2" data-testid="applied-coupon">
              <span className="text-brand-maroon font-medium">{appliedCoupon.code} applied</span>
              <button onClick={removeCoupon} className="text-brand-muted hover:text-brand-maroon" data-testid="remove-coupon-btn">Remove</button>
            </div>
          )}
          <p className="text-[11px] text-brand-muted mb-4">Only code: BINDNI10 · 10% off on orders above ₹999</p>

          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-brand-muted">Subtotal</span><span data-testid="cart-subtotal">{formatINR(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-brand-muted">Shipping</span><span>{shipping === 0 ? "Free" : formatINR(shipping)}</span></div>
            {discount > 0 && <div className="flex justify-between text-brand-maroon"><span>Discount ({appliedCoupon.code})</span><span>-{formatINR(discount)}</span></div>}
            <div className="gold-divider" />
            <div className="flex justify-between font-semibold text-base pt-2"><span>Total</span><span className="text-brand-maroon text-lg" data-testid="cart-total">{formatINR(total)}</span></div>
          </div>

          <button onClick={() => nav("/checkout")} className="btn-primary w-full mt-8" data-testid="checkout-btn">Proceed to Checkout</button>
          <p className="text-[11px] text-brand-muted mt-4 text-center">Secure checkout · UPI, Cards, Wallets, COD</p>
        </aside>
      </div>
    </div>
  );
}
